import { KM_TO_M, MILES_TO_M } from '../const'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { addColumn } from '../utils/schemas'
import { SelectWorkflowComponent } from './common/selectcomponent'

class LineInterpolatePoint extends SelectWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.lineinterpolatepoint',
        group: 'Spatial Operations',
        title: 'ST Line Interpolate Point',
        description: `
      Takes a line and returns points separated at a specified distance along the line.
      `
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'geo',
          type: WorkflowComponentParamType.Column,
          title: 'Lines column',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'distance',
          type: WorkflowComponentParamType.Number,
          title: 'Distance',
          default: 0
        },
        {
          name: 'units',
          type: WorkflowComponentParamType.Selection,
          title: 'Units',
          options: ['Meters', 'Miles'],
          default: 'Meters'
        }
      ]
    )
  }

  getInnerSql({ params, connection, envVars }: WorkflowComponentCodeParams) {
    let convert =
      params.units === 'Meters'
        ? `/ ${KM_TO_M}`
        : ` * ${MILES_TO_M} / ${KM_TO_M}`
    switch (connection.type) {
      case Provider.BigQuery:
        return `SELECT
                  ${connection.quoteName(
                    envVars.analyticsToolboxDataset as string
                  )}.ST_LINE_INTERPOLATE_POINT(
                    ${params.geo},
                    ${params.distance} ${convert},
                    NULL
                  ) AS ${params.geo}_interpolated, *
                FROM ${connection.quoteName(params.source as string)}`
      case Provider.Snowflake:
        convert =
          params.units === 'Meters'
            ? `/ ${KM_TO_M}`
            : ` * ${MILES_TO_M} / ${KM_TO_M}`
        return `SELECT
                  ${connection.quoteName(
                    envVars.analyticsToolboxDataset as string
                  )}.ST_LINE_INTERPOLATE_POINT(
                    ${params.geo},
                    ${params.distance} ${convert}
                  ) AS ${params.geo}_interpolated, *
                FROM ${connection.quoteName(params.source as string)}`
      case Provider.Postgres:
      case Provider.Redshift:
        convert = params.units === 'Meters' ? '' : ` * ${MILES_TO_M}`
        return `SELECT
                  ST_LINEINTERPOLATEPOINT(
                    ${params.geo},
                    LEAST(
                      ${params.distance} ${convert}
                        / ST_LENGTH(${params.geo}::geography),
                      1
                    )
                  ) AS ${params.geo}_interpolated, *
                FROM ${connection.quoteName(params.source as string)}`
      default:
        return null
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    switch (provider) {
      case Provider.BigQuery:
      case Provider.Snowflake:
        return '1.0.0'
      default:
        return null
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({
    columns,
    params,
    connection
  }: GetOutputSchemaParams): TableSchema {
    return addColumn(columns.source, {
      name: `${params.geo}_interpolated`,
      type: connection.getGeoDataType()
    })
  }
  supportedProviders(): Provider[] {
    return [
      Provider.BigQuery,
      Provider.Snowflake,
      Provider.Redshift,
      Provider.Postgres
    ]
  }
}

export { LineInterpolatePoint }
